'use client';

import { useEffect, useRef, useState } from 'react';

type Props = {
  value: string; // 이미 억/만 단위로 축약된 표시용 숫자(예: "128.4", "6,800")
  className?: string;
};

// 화면에 들어오는 순간 0에서 목표값까지 올라가는 카운트업.
// - value는 표시 문자열(콤마·소수점 포함)이라 애니메이션은 같은 자릿수
//   포맷의 숫자로 보간하고, 마지막 프레임에 원래 문자열을 그대로 꽂는다.
// - prefers-reduced-motion이면 애니메이션 없이 바로 최종값을 보여준다.
export default function AnimatedCount({ value, className }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState('0');
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const target = Number(value.replace(/,/g, ''));
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotion || Number.isNaN(target)) {
      setDisplay(value);
      return;
    }

    const decimals = value.includes('.') ? value.split('.')[1].length : 0;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || started.current) return;
        started.current = true;

        const duration = 900;
        const start = performance.now();

        const tick = (now: number) => {
          const progress = Math.min((now - start) / duration, 1);
          // easeOutCubic — 빠르게 올라가다 목표값 근처에서 서서히 멈춘다.
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = target * eased;

          setDisplay(
            progress < 1
              ? current.toLocaleString('ko-KR', {
                  minimumFractionDigits: decimals,
                  maximumFractionDigits: decimals
                })
              : value
          );

          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
